import { Funcionario } from '@/context/FuncionariosContext';
import { calcularProgressao, getStatusColor } from '@/utils/progressaoUtils';
import { useAppSettings } from '@/context/AppSettingsContext';

function calcularAnosServico(dataEntrada?: string): number | null {
  if (!dataEntrada) return null;
  const entrada = new Date(dataEntrada);
  if (isNaN(entrada.getTime())) return null;
  const hoje = new Date();
  let anos = hoje.getFullYear() - entrada.getFullYear();
  const mesAtual = hoje.getMonth();
  const mesEntrada = entrada.getMonth();
  if (mesAtual < mesEntrada || (mesAtual === mesEntrada && hoje.getDate() < entrada.getDate())) anos -= 1;
  return Math.max(0, anos);
}

interface FuncionarioCardProps {
  funcionario: Funcionario;
  index: number;
  onClick: () => void;
  avaliacoesCount?: number;
  pontosCalculados?: number;
  limiar?: number;
}

const avatarPalette = [
  { bg: 'bg-rose-100', text: 'text-rose-600' },
  { bg: 'bg-violet-100', text: 'text-violet-600' },
  { bg: 'bg-amber-100', text: 'text-amber-600' },
  { bg: 'bg-teal-100', text: 'text-teal-600' },
  { bg: 'bg-cyan-100', text: 'text-cyan-600' },
  { bg: 'bg-orange-100', text: 'text-orange-600' },
];

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

export default function FuncionarioCard({
  funcionario,
  index,
  onClick,
  avaliacoesCount = 0,
  pontosCalculados,
  limiar: limiarProp,
}: FuncionarioCardProps) {
  const { settings } = useAppSettings();
  const palette = avatarPalette[index % avatarPalette.length];

  const initials = funcionario.nome
    .split(' ')
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((n) => n[0])
    .join('');

  const pontosEfetivos = pontosCalculados ?? funcionario.pontosAtuais ?? 0;
  const limiarEfetivo = limiarProp ?? settings.pontos_progressao;
  const progressao = calcularProgressao(pontosEfetivos, funcionario.nivelAtual, limiarEfetivo);
  const temProgressaoRegistada = (funcionario.pontosDescontados ?? 0) > 0;
  const statusColorRaw = getStatusColor(progressao.status);
  const statusColor =
    temProgressaoRegistada && progressao.status === 'pronto'
      ? getStatusColor('em_progresso')
      : statusColorRaw;

  const progressoesConsecutivas = Math.floor(pontosEfetivos / limiarEfetivo);
  const temSegundaProgressao = progressoesConsecutivas >= 2;
  const anosServico = calcularAnosServico(funcionario.dataEntradaFuncaoPublica);

  const isElegivel = progressao.status === 'pronto' && !temProgressaoRegistada;

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white rounded-xl border border-zinc-100 hover:border-zinc-200 transition-all cursor-pointer group overflow-hidden"
    >
      {/* Status bar top */}
      <div className={`h-[3px] w-full ${statusColor.bar} ${isElegivel ? 'opacity-100' : 'opacity-30'}`} />

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3.5">
          <div
            className={`w-10 h-10 flex items-center justify-center rounded-full text-[13px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-zinc-800 leading-tight truncate">{funcionario.nome}</p>
            <p className="text-[11px] text-zinc-400 mt-0.5 font-mono">#{funcionario.id}</p>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            {temSegundaProgressao && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                <i className="ri-repeat-line text-[10px]" />
                2ª Prog.
              </span>
            )}
            {avaliacoesCount > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded-full">
                <i className="ri-bar-chart-line text-[10px]" />
                {avaliacoesCount}
              </span>
            )}
          </div>
        </div>

        {/* Carreira + Unidade */}
        <div className="mb-3.5 space-y-1">
          <div className="flex items-center gap-1.5">
            <div className="w-3.5 h-3.5 flex items-center justify-center text-zinc-300 flex-shrink-0">
              <i className="ri-briefcase-line text-xs" />
            </div>
            <span className="text-[11px] text-zinc-500 font-medium truncate">{funcionario.carreira}</span>
          </div>
          {funcionario.unidadeOrganica && (
            <div className="flex items-center gap-1.5">
              <div className="w-3.5 h-3.5 flex items-center justify-center text-zinc-300 flex-shrink-0">
                <i className="ri-building-line text-xs" />
              </div>
              <span className="text-[11px] text-zinc-400 truncate">{funcionario.unidadeOrganica}</span>
            </div>
          )}
        </div>

        {/* Progressão */}
        <div className={`rounded-lg px-3 py-2.5 mb-3.5 ${isElegivel ? 'bg-emerald-50' : 'bg-zinc-50'}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className={`text-[11px] font-semibold ${isElegivel ? 'text-emerald-700' : 'text-zinc-500'}`}>
              {isElegivel
                ? 'Elegível para progressão'
                : progressao.status === 'pronto' && temProgressaoRegistada
                ? `${progressao.pontosAtuais} pts acumulados`
                : `Faltam ${progressao.pontosFaltam.toFixed(1)} pts`}
            </span>
            <span className={`text-[11px] font-bold ${isElegivel ? 'text-emerald-700' : 'text-zinc-500'}`}>
              {progressao.pontosAtuais}/{progressao.pontosNecessarios}
            </span>
          </div>
          <div className="h-1 bg-white rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${statusColor.bar}`}
              style={{ width: `${progressao.percentagem}%` }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
          <div className="flex items-center gap-1.5">
            <div className="w-3.5 h-3.5 flex items-center justify-center text-zinc-300">
              <i className="ri-money-euro-circle-line text-xs" />
            </div>
            <span className="text-[11px] font-semibold text-zinc-600">
              {(funcionario.vencimento ?? 0).toLocaleString('pt-PT', {
                style: 'currency',
                currency: 'EUR',
                maximumFractionDigits: 0,
              })}
            </span>
          </div>
          {anosServico !== null ? (
            <div className="flex items-center gap-1">
              <span className="text-[11px] font-semibold text-zinc-600">{anosServico}</span>
              <span className="text-[11px] text-zinc-400">anos</span>
            </div>
          ) : (
            <span className="text-[11px] text-zinc-400">{formatDate(funcionario.ultimaProgressao)}</span>
          )}
        </div>

        {/* Hover hint */}
        <p className="text-[11px] text-zinc-400 mt-2.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <i className="ri-history-line text-xs" />
          Ver histórico
        </p>
      </div>
    </button>
  );
}
